import React, { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import { MdHistory } from './Icons';

export type VideoCardProps = {
  poster: string;
  onClick?: () => void;
  name: string;
  organization?: string;
  author: string;
  updateDate: string; // YYYY/M/D
  isMobile?: boolean;
};

function VideoCard({
  onClick,
  poster,
  organization,
  author,
  name,
  updateDate,
  isMobile,
}: VideoCardProps) {
  const videoWrapperRef = useRef<HTMLDivElement>(null);
  const [videoWrapperHeight, setVideoWrapperHeight] = useState(0);

  useEffect(() => {
    if (videoWrapperRef.current?.offsetWidth) {
      setVideoWrapperHeight((videoWrapperRef.current.offsetWidth / 16) * 9);
    }
  }, [videoWrapperRef.current?.offsetWidth]);

  const image = (
    <NextImage src={poster} alt={name} layout="fill" objectFit="contain" />
  );
  const playIcon = (
    <NextImage
      src="/images/icon_video_play_button.svg"
      width={48}
      height={48}
    />
  );

  const date = (
    <Flex pb={2} gap={0.5}>
      <MdHistory color="#999999" size="1.1em" />
      <Text as="span" fontSize="xs">
        <time dateTime={updateDate.replaceAll('/', '-')}>{updateDate}</time>
      </Text>
    </Flex>
  );

  if (isMobile) {
    return (
      <Flex
        boxShadow="0 0 8px rgba(0,0,0,0.08)"
        borderRadius="lg"
        overflow="hidden"
        onClick={() => onClick?.()}
      >
        <Flex pl={2} flex={1} flexDirection="column">
          <Box
            my={2}
            fontWeight="bold"
            noOfLines={2}
            fontSize="sm"
            lineHeight={1.25}
            maxHeight="2.6em"
          >
            {name}
          </Box>
          <Flex
            flexDirection="column"
            mb={1}
            whiteSpace="pre-wrap"
            fontSize="xs"
            lineHeight={1.25}
            maxHeight="2.5em"
            flex={1}
          >
            {!!organization && <Box noOfLines={1}>{organization}</Box>}
            <Box noOfLines={1}>{author}</Box>
          </Flex>
          {date}
        </Flex>
        <HStack align="flex-start" h={110} p={2} spacing={2} pos="relative">
          <Box
            h={90}
            w={120}
            bg="blackAlpha.300"
            borderWidth={1}
            borderColor="gray.50"
            borderRadius="md"
            overflow="hidden"
            pos="relative"
          >
            {image}
            <Flex
              pos="absolute"
              bottom={0}
              left={0}
              top={0}
              right={0}
              mt={1}
              justifyContent="center"
              alignItems="center"
            >
              {playIcon}
            </Flex>
          </Box>
        </HStack>
      </Flex>
    );
  }

  return (
    <Flex
      onClick={() => onClick?.()}
      pos="relative"
      flexDirection="column"
      boxShadow="0px 4px 4px rgba(0, 0, 0, 0.25)"
      borderRadius="md"
      overflow="hidden"
      cursor="pointer"
    >
      <Box
        ref={videoWrapperRef}
        height={videoWrapperHeight}
        width="100%"
        position="relative"
        bg="blackAlpha.300"
        borderBottomColor="gray.50"
        borderBottomWidth={1}
      >
        {image}
        <Box pos="absolute" bottom={0.5} left={1.5} lineHeight={0}>
          {playIcon}
        </Box>
      </Box>
      <Box px={4}>
        <Box my={2} fontWeight="bold" noOfLines={2} minH="3em" fontSize="sm">
          {name}
        </Box>
        <Flex flexDirection="column" mb={2} fontSize="xs" minH="3em">
          {!!organization && <Box noOfLines={1}>{organization}</Box>}
          <Box noOfLines={1}>{author}</Box>
        </Flex>
        {date}
      </Box>
    </Flex>
  );
}

export default VideoCard;
